import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { CatDrawer } from "blocks-nextjs/blocks-react/components/Drawer";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import React, { useCallback, useState } from "react";
import { formatLastReportedTime as formatProjectLastReportedTime, getAssetImageIcon, isLastReportedTimeStale } from "../../pages/Projects/ProjectDetails.helper";
import { useDeleteAssetFromSite, useIgnoreAssetFromSite, useRecoverAssetFromSite } from "../../services/query/delete-asset-from-site.query";
import { useUpdateProjectAssetDetails } from "../../services/query/updates-project-asset-details.query";
import useAppConfigStore from "../../store/useAppConfigStore";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { Variant } from "../GlobalNotification/GlobalNotification";
import UnknownMachineHeaderDetails from "../UnknownMashine/UnknownMachineHeaderDetails";
import UnknownMachineHistoricalInfo from "../UnknownMashine/UnknownMachineHistoricalInfo";
import UnknownMachineLiveView from "../UnknownMashine/UnknownMachineLiveView";
import ProductivityStatCards from "../history/ProductivityStatCards";
const NOTIFICATION_CONFIGS = {
    deleteError: {
        message: "Error. Failed to delete asset. Please try later",
        showNotification: true,
        type: Variant.error
    },
    deleteSuccess: {
        message: "Success. Asset deleted from site",
        showNotification: true,
        type: Variant.success
    },
    ignoreError: {
        message: "Error. Failed to ignore asset. Please try later",
        showNotification: true,
        type: Variant.error
    },
    ignoreSuccess: {
        message: "Success. Asset ignored from site",
        showNotification: true,
        type: Variant.success
    },
    recoverError: {
        message: "Error. Failed to recover asset. Please try later",
        showNotification: true,
        type: Variant.error
    },
    recoverSuccess: {
        message: "Success. Asset recovered from site",
        showNotification: true,
        type: Variant.success
    },
    updateError: {
        message: "Error. Update failed. Please try later",
        showNotification: true,
        type: Variant.error
    },
    updateSuccess: {
        message: "Success. Asset details updated successfully",
        showNotification: true,
        type: Variant.success
    }
};
const UnknownMachineDrawer = ({ assetDrawerActive, setAssetDrawerActive }) => {
    const queryClient = useQueryClient();
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const { primaryPartyType, setGlobalNotification, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        setGlobalNotification: state.setGlobalNotification,
        userPartyNumber: state.userPartyNumber
    }));
    const { assetDetails, projectMetaData, projectName } = useProjectDetailsStore((state) => ({
        assetDetails: state.assetDetails,
        projectMetaData: state.projectMetaData,
        projectName: state.projectDetails?.projectName
    }));
    const queryParamsUpdateProjectAssetInfo = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber }),
        operationType: "append"
    };
    const partyQueryParams = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber })
    };
    const projectId = projectMetaData?.projectId || "";
    const { mutate } = useUpdateProjectAssetDetails(projectId, queryParamsUpdateProjectAssetInfo);
    const { mutate: mutateDeleteAsset } = useDeleteAssetFromSite(projectId, partyQueryParams);
    const { mutate: mutateIgnoreAsset } = useIgnoreAssetFromSite(projectId, partyQueryParams);
    const { mutate: mutateRecoverAsset } = useRecoverAssetFromSite(projectId, partyQueryParams);
    const handleUnknownAssetDetailsSave = (updateAssetDetails) => {
        if (!assetDetails?.assetId || !projectId)
            return;
        mutate({
            assetDetails: [
                {
                    assetId: updateAssetDetails.assetId,
                    assetName: updateAssetDetails.assetName,
                    assetSerialNumber: updateAssetDetails.assetSerialNumber,
                    assetSourceType: updateAssetDetails.assetCategory
                }
            ]
        }, {
            onError: () => {
                setGlobalNotification(NOTIFICATION_CONFIGS.updateError);
            },
            onSuccess: () => {
                setGlobalNotification(NOTIFICATION_CONFIGS.updateSuccess);
                queryClient.invalidateQueries({ queryKey: ["projectAssetsDetails", projectId] });
                queryClient.invalidateQueries({ queryKey: ["projectNetwork", projectId] });
            }
        });
    };
    const handleDeleteFromSite = () => {
        if (!assetDetails?.assetId || !projectId)
            return;
        mutateDeleteAsset(assetDetails.assetId, {
            onError: () => {
                setGlobalNotification(NOTIFICATION_CONFIGS.deleteError);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["projectAssetsDetails", projectId] });
                queryClient.invalidateQueries({ queryKey: ["projectNetwork", projectId] });
                setAssetDrawerActive(false);
                setGlobalNotification(NOTIFICATION_CONFIGS.deleteSuccess);
            }
        });
    };
    const handleIgnoreFromSite = () => {
        if (!assetDetails?.assetId || !projectId)
            return;
        mutateIgnoreAsset(assetDetails.assetId, {
            onError: () => {
                setGlobalNotification(NOTIFICATION_CONFIGS.ignoreError);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["projectAssetsDetails", projectId] });
                queryClient.invalidateQueries({ queryKey: ["projectNetwork", projectId] });
                setAssetDrawerActive(false);
                setGlobalNotification(NOTIFICATION_CONFIGS.ignoreSuccess);
            }
        });
    };
    const handleRecover = () => {
        if (!assetDetails?.assetId || !projectId)
            return;
        mutateRecoverAsset(assetDetails.assetId, {
            onError: () => {
                setGlobalNotification(NOTIFICATION_CONFIGS.recoverError);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["projectAssetsDetails", projectId] });
                queryClient.invalidateQueries({ queryKey: ["projectNetwork", projectId] });
                setAssetDrawerActive(false);
                setGlobalNotification(NOTIFICATION_CONFIGS.recoverSuccess);
            }
        });
    };
    const timezone = projectMetaData?.timeZone || projectMetaData?.timezone || projectMetaData?.offset;
    const isStale = !!assetDetails?.timestamp && isLastReportedTimeStale(assetDetails.timestamp, timezone);
    const reportingStatus = timezone && isStale ? "Not Reporting" : "Reporting";
    const formattedLastReportedTime = formatProjectLastReportedTime(assetDetails?.location?.lastReportedTime, timezone, appConfig?.release_1_1?.showCestTimezone);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleDateRangeResolved = useCallback((_payload) => { }, []);
    const [dischargeEnergySum, setDischargeEnergySum] = useState("-");
    const [isEnergyStatLoading, setIsEnergyStatLoading] = useState(false);
    const handleEnergyDataChange = useCallback((totalDischarge, isLoading) => {
        setDischargeEnergySum(totalDischarge || "-");
        setIsEnergyStatLoading(isLoading);
    }, []);
    return (_jsx(CatDrawer, { "data-test": "unknown-machine-drawer", disableBackdropClick: false, disableEscClick: true, hasBackdrop: true, isActive: assetDrawerActive, onBlDrawerClose: () => {
            setAssetDrawerActive(false);
        }, onBlDrawerOpen: () => { }, preserveContent: false, width: "63%", children: _jsxs(_Fragment, { children: [_jsx("div", { "data-test": "unknown-machine-drawer-header", slot: "header", children: _jsx(CatHeading, { "data-test": "unknown-machine-drawer-title", level: "h1", variant: "title", children: "Asset Details" }) }), _jsxs("div", { className: "-m-4 pb-4", children: [_jsx(UnknownMachineHeaderDetails, { activity: assetDetails?.activity || "Unknown", assetId: assetDetails?.assetId, assetName: assetDetails?.assetName, capacity: assetDetails?.capacity ?? -1, iconUrl: getAssetImageIcon(assetDetails), onApplyChanges: handleUnknownAssetDetailsSave, projectName: projectName || "", reportingStatus: reportingStatus, serialNumber: assetDetails?.assetSerialNumber || "", streetAddress: assetDetails?.location?.streetAddress || "" }), _jsx(UnknownMachineLiveView, { activity: assetDetails?.activity, assetIdentifierStatus: assetDetails?.assetIdentifierStatus, assetLatestPower: assetDetails?.assetLatestPower, assetSourceType: assetDetails?.assetSourceType, assetStatus: assetDetails?.assetStatus, batteryLevel: assetDetails?.soc !== undefined && assetDetails?.soc !== null
                                ? Number(assetDetails?.soc)
                                : undefined, formattedLastReportedTime: formattedLastReportedTime, hideBatteryLevel: assetDetails?.assetSourceType === "GHOST" && isStale, onConfirmDelete: handleDeleteFromSite, onConfirmIgnore: handleIgnoreFromSite, onConfirmRecover: handleRecover, timeToReady: assetDetails?.timeToReady, timeToStopCharge: assetDetails?.timeToStopCharge }), _jsx(UnknownMachineHistoricalInfo, { assetDrawerActive: assetDrawerActive, assetId: assetDetails?.assetId, onDateRangeResolved: handleDateRangeResolved, onEnergyDataChange: handleEnergyDataChange, projectId: projectMetaData?.projectId, projectTimezone: projectMetaData?.timezone, statCards: _jsx("div", { className: "flex items-center justify-center min-h-5 space-x-4", "data-test": "productivity-stat-cards-historical", children: _jsx(ProductivityStatCards, { dischargeEnergySum: dischargeEnergySum, dischargeHoursSum: "-", isLoading: isEnergyStatLoading, showDischargeTooltip: false }) }), timezoneOffset: projectMetaData?.offset || "UTC+00:00" })] })] }) }));
};
export default UnknownMachineDrawer;
